import { css } from 'styled-components';
import Colr from 'colr';
import { buttonReset, getSmallButtonTokens, getMediumButtonTokens, getLargeButtonTokens, getPrimaryButtonTokens, getPrimaryButtonSelectedTokens, getPrimaryButtonDisabledTokens, getSecondaryButtonTokens, getSecondaryButtonSelectedTokens, getSecondaryButtonDisabledTokens, getTertiaryButtonTokens, getTertiaryButtonSelectedTokens, getTertiaryButtonDisabledTokens, getDestructiveButtonTokens, getDestructiveButtonSelectedTokens, getDestructiveButtonDisabledTokens } from '../shared';
import common, { spacing } from '../../common';
import token from '../../getToken';
export const hoverColor = color => Colr.fromHex(color).darken(10).toHex();
export const activeColor = color => Colr.fromHex(color).darken(20).toHex();
export const disabledTextColor = color => {
  const rgb = Colr.fromHex(color).toRgbArray().toString();
  return `rgba(${rgb}, .3)`;
};
const typeScale = {
  1: '2.016rem',
  2: '1.764rem',
  3: '1.512rem',
  4: '1.26rem',
  5: '1.134rem',
  6: '1.008rem',
  7: '0.75rem'
};
const lineHeights = [1, 1.2, 1.3, 1.6];
const tracking = {
  normal: 'normal'
};
const fonts = {
  futura: '"Futura BT", "Futura Book", "Futura", "Avant Garde", arial, sans-serif'
};
const colors = {
  brand: {
    base: '#CC0000',
    light: '#CC0000',
    dark: '#850000'
  },
  accent: '#92B605',
  shades: {
    black: '#000000',
    darkest: '#2A2A2A',
    darker: '#535353',
    dark: '#999',
    light: '#E5E5E5',
    lighter: '#ebe9e9',
    lightest: '#f4f4f4',
    nearWhite: '#f7f6f6',
    white: '#ffffff'
  },
  focus: '#dbeaf0',
  error: '#850000',
  success: '#92B605',
  info: '#005bb9',
  product: {
    hospital: '#850000',
    extras: '#850000',
    combined: '#850000',
    highlight: {
      backgroundColor: '#cc0000',
      textColor: '#ffffff'
    }
  }
};
const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '2px',
  outlineOffset: '0.125em'
};
const baseButtonStyles = css`
  ${buttonReset};

  font-family: ${fonts.futura};
  font-weight: 600;
  border-radius: ${token('button.dimension.border.radius', '4px')};
  border-style: solid;
  border-width: ${token('button.dimension.border.width', '1px')};

  &:disabled {
    border: dashed 1px #999;
    opacity: 0.55;
    cursor: no-drop;
  }
`;
const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles};
      ${getPrimaryButtonTokens({
      bg: {
        default: colors.accent,
        hover: '#759204',
        active: colors.accent,
        disabled: colors.accent
      },
      fg: {
        default: colors.shades.white,
        hover: colors.shades.white,
        active: colors.shades.white,
        disabled: colors.shades.white
      },
      border: {
        default: colors.accent,
        hover: colors.accent,
        active: colors.accent,
        disabled: colors.accent
      }
    })};
      background-image: linear-gradient(180deg, #6da300 0, #558000 100%);
      box-shadow: 0 2px 4px 0 rgb(0 0 0 / 50%);

      &:hover:not([disabled]) {
        background-image: none;
      }

      &:active:not([disabled]) {
        background-image: none;
      }
    `,
    selected: css`
      ${getPrimaryButtonSelectedTokens({
      bg: colors.accent,
      fg: colors.shades.white,
      border: colors.accent
    })}
    `,
    disabled: css`
      ${getPrimaryButtonDisabledTokens({
      bg: colors.accent,
      fg: colors.shades.white,
      border: '#667f04'
    })}
    `
  },
  secondary: {
    normal: css`
      ${baseButtonStyles}
      ${getSecondaryButtonTokens({
      bg: {
        default: colors.shades.white,
        hover: '#eff4da',
        active: colors.shades.white,
        disabled: colors.shades.white
      },
      fg: {
        default: colors.accent,
        hover: colors.accent,
        active: colors.accent,
        disabled: colors.accent
      },
      border: {
        default: colors.accent,
        hover: colors.accent,
        active: colors.accent,
        disabled: colors.accent
      }
    })};
    `,
    selected: css`
      ${getSecondaryButtonSelectedTokens({
      bg: colors.shades.white,
      fg: colors.accent,
      border: colors.accent
    })}
    `,
    disabled: css`
      ${getSecondaryButtonDisabledTokens({
      bg: colors.shades.white,
      fg: colors.accent,
      border: colors.accent
    })}
    `
  },
  tertiary: {
    normal: css`
      ${baseButtonStyles}
      ${getTertiaryButtonTokens({
      bg: {
        default: colors.shades.white,
        hover: '#eff4da',
        active: colors.shades.white,
        disabled: colors.shades.white
      },
      fg: {
        default: colors.accent,
        hover: colors.accent,
        active: colors.accent,
        disabled: colors.accent
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      }
    })};
    `,
    selected: css`
      ${getTertiaryButtonSelectedTokens({
      bg: '#eff4da',
      fg: colors.accent,
      border: 'transparent'
    })};
    `,
    disabled: css`
      ${getTertiaryButtonDisabledTokens({
      bg: colors.shades.white,
      fg: colors.accent,
      border: 'transparent'
    })};
      border: none;
    `
  },
  destructive: {
    normal: css`
      ${baseButtonStyles}
      ${getDestructiveButtonTokens({
      bg: {
        default: colors.shades.white,
        hover: '#e7cccc',
        active: colors.shades.white,
        disabled: colors.shades.white
      },
      fg: {
        default: colors.error,
        hover: colors.error,
        active: colors.error,
        disabled: colors.error
      },
      border: {
        default: colors.error,
        hover: colors.error,
        active: colors.error,
        disabled: colors.error
      }
    })};
    `,
    selected: css`
      ${getDestructiveButtonSelectedTokens({
      bg: colors.shades.white,
      fg: colors.error,
      border: colors.error
    })};
    `,
    disabled: css`
      ${getDestructiveButtonDisabledTokens({
      bg: colors.shades.white,
      fg: colors.error,
      border: colors.error
    })};
    `
  },
  small: css`
    ${getSmallButtonTokens({
    fontFamily: fonts.futura,
    fontSize: typeScale[6],
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[2],
    paddingInline: spacing[2],
    gap: 'unset'
  })}
  `,
  medium: css`
    ${getMediumButtonTokens({
    fontFamily: fonts.futura,
    fontSize: typeScale[6],
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[3],
    paddingInline: spacing[3],
    gap: 'unset'
  })}
  `,
  large: css`
    ${getLargeButtonTokens({
    fontFamily: fonts.futura,
    fontSize: typeScale[5],
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[3],
    paddingInline: '25px',
    gap: 'unset'
  })}
  `
};
const baseFormValues = {
  borderColor: colors.shades.dark,
  borderColorValid: colors.success,
  borderColorInvalid: colors.error,
  selectedToggleColor: colors.accent,
  helpTextColor: colors.shades.darker,
  helpTextSize: typeScale[7],
  labelColor: colors.shades.darker,
  labelSize: typeScale[6],
  errorMessageTextColor: colors.error,
  infoMessageTextColor: colors.info
};
const AAMITheme = {
  ...common,
  typeScale,
  fonts,
  colors,
  focus,
  id: 'aami',
  mode: {
    form: 'light' //default
  },
  headings: {
    color: 'currentColor',
    h1: {
      fontFamily: fonts.futura,
      fontSize: typeScale[1],
      lineHeight: lineHeights[1],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.futura,
      fontSize: typeScale[2],
      lineHeight: lineHeights[2],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.futura,
      fontSize: typeScale[3],
      lineHeight: lineHeights[2],
      fontWeight: 600,
      tracking: tracking.normal,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.futura,
      fontSize: typeScale[4],
      lineHeight: lineHeights[2],
      fontWeight: 600,
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.futura,
      fontSize: typeScale[6],
      lineHeight: lineHeights[3],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: true
    },
    h6: {
      fontFamily: fonts.futura,
      fontSize: typeScale[6],
      lineHeight: lineHeights[2],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: true
    }
  },
  copy: {
    color: colors.shades.darker,
    fontFamily: fonts.futura,
    fontSize: typeScale[6],
    fontWeight: 400,
    lineHeight: lineHeights[3],
    smallFontSize: typeScale[7],
    largeFontSize: typeScale[5]
  },
  link: {
    loudColor: colors.brand.light,
    darkColor: colors.shades.darker,
    lightColor: colors.shades.white,
    destructiveColor: colors.error
  },
  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: buttonStyles.primary,
    secondary: buttonStyles.secondary,
    tertiary: buttonStyles.tertiary,
    destructive: buttonStyles.destructive
  },
  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `0 4px 8px 0 ${colors.shades.lighter}`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  },
  smallFooter: {
    backgroundColor: colors.shades.white,
    textColor: colors.shades.darkest,
    linkColor: colors.brand.base
  },
  panel: {
    loud: {
      backgroundColor: colors.brand.base,
      textColor: colors.shades.white
    },
    muted: {
      backgroundColor: colors.shades.lighter,
      textColor: colors.shades.darkest
    },
    light: {
      backgroundColor: colors.shades.lightest,
      textColor: colors.shades.darkest
    },
    dark: {
      backgroundColor: colors.shades.light,
      textColor: colors.shades.white
    },
    white: {
      backgroundColor: colors.shades.white,
      textColor: colors.shades.darkest
    }
  }
};
export default AAMITheme;